import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import createElementPDF from "@/publicjs/createElementPDF";
import { errorActions } from "@/alerts";
import { getCompany } from "@/publicjs/company";
import { imageInBase64 } from "@/publicjs/logoCompany";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const formatDateTime = (value) => {
  if (!value) {
    return "No disponible";
  }

  return new Intl.DateTimeFormat("es-DO", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const normalizeText = (value, fallback = "No disponible") => {
  const normalized = String(value || "").trim();
  return normalized || fallback;
};

const formatMoney = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "0.00";
  }

  return new Intl.NumberFormat("es-DO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const buildKeyValueTable = (rows) => ({
  table: {
    widths: [105, "*"],
    body: rows.map(([label, value]) => [
      {
        text: label,
        bold: true,
        fillColor: "#eef2f7",
        margin: [6, 6, 6, 6],
      },
      {
        text: normalizeText(value),
        margin: [6, 6, 6, 6],
      },
    ]),
  },
  layout: {
    hLineColor: () => "#d7dee7",
    vLineColor: () => "#d7dee7",
  },
});

const buildCompanyLines = (company, includeStore = true) => {
  const lines = [
    normalizeText(company?.companyLegalName || company?.companyName, "Empresa"),
    normalizeText(company?.companyAddress),
  ];

  if (company?.companyEmail) {
    lines.push(`Correo: ${company.companyEmail}`);
  }

  if (company?.companycellphone) {
    lines.push(`Tel: ${company.companycellphone}`);
  }

  if (company?.companyRUC) {
    lines.push(`RNC: ${company.companyRUC}`);
  }

  if (includeStore && company?.storeName) {
    lines.push(`Sucursal: ${company.storeName}`);
  }

  return lines;
};

const resolveOrderDetails = (order) => {
  const details = Array.isArray(order?.details) ? order.details : [];

  if (details.length) {
    return details;
  }

  if (order?.articulo_referencia) {
    return [
      {
        descripcion: order.articulo_referencia,
        cantidad: 1,
        precio_unitario: Number(order?.precio_articulo_referencia || 0),
      },
    ];
  }

  return [];
};

const resolveEstimatedTotal = (order) => {
  if (order?.presupuesto !== null && order?.presupuesto !== undefined) {
    return Number(order.presupuesto || 0);
  }

  const details = resolveOrderDetails(order);
  const detailsTotal = details.reduce((accumulator, detail) => {
    const quantity = Number(detail?.cantidad || 0);
    const unitPrice = Number(detail?.precio_unitario || 0);
    return (
      accumulator +
      (Number.isFinite(quantity) && Number.isFinite(unitPrice)
        ? Math.max(quantity, 0) * Math.max(unitPrice, 0)
        : 0)
    );
  }, 0);

  return detailsTotal;
};

const buildDetailsSummaryText = (order) => {
  const details = resolveOrderDetails(order);

  if (!details.length) {
    return "Sin conceptos registrados";
  }

  return details
    .map((detail) => {
      const quantity = Number(detail?.cantidad || 0);
      const unitPrice = Number(detail?.precio_unitario || 0);
      const subtotal = Math.max(quantity, 0) * Math.max(unitPrice, 0);
      return `${normalizeText(detail?.descripcion)} x${formatMoney(
        quantity,
      )} · ${formatMoney(unitPrice)} = ${formatMoney(subtotal)}`;
    })
    .join("\n");
};

const buildLetterDefinition = ({
  company,
  companyLogo,
  order,
  publicTrackingUrl,
  publicTrackingPath,
  equipmentName,
}) => ({
  pageSize: "LETTER",
  pageMargins: [32, 36, 32, 42],
  content: [
    {
      columns: [
        companyLogo
          ? {
              width: 72,
              image: companyLogo,
              fit: [58, 58],
            }
          : {
              width: 72,
              text: "",
            },
        {
          width: "*",
          stack: buildCompanyLines(company).map((line, index) => ({
            text: line,
            fontSize: index === 0 ? 15 : 9,
            bold: index === 0,
            color: index === 0 ? "#0f172a" : "#475569",
            margin: index === 0 ? [0, 0, 0, 0] : [0, 2, 0, 0],
          })),
        },
        {
          width: 190,
          table: {
            widths: ["*"],
            body: [
              [
                {
                  stack: [
                    {
                      text: "RECIBO DE RECEPCION",
                      alignment: "center",
                      bold: true,
                      fontSize: 11,
                      color: "#0f172a",
                      margin: [0, 4, 0, 0],
                    },
                    {
                      text: normalizeText(order.codigo, `Entrada #${order.id}`),
                      alignment: "center",
                      bold: true,
                      fontSize: 15,
                      color: "#1d4ed8",
                      margin: [0, 6, 0, 0],
                    },
                    {
                      text: `Fecha: ${formatDateTime(order.fecha_recepcion)}`,
                      alignment: "center",
                      fontSize: 9,
                      color: "#475569",
                      margin: [0, 6, 0, 4],
                    },
                  ],
                },
              ],
            ],
          },
          layout: {
            hLineColor: () => "#cbd5e1",
            vLineColor: () => "#cbd5e1",
            hLineWidth: () => 1,
            vLineWidth: () => 1,
          },
        },
      ],
      columnGap: 14,
    },
    {
      canvas: [
        {
          type: "line",
          x1: 0,
          y1: 0,
          x2: 548,
          y2: 0,
          lineWidth: 1,
          lineColor: "#d7dee7",
        },
      ],
      margin: [0, 14, 0, 14],
    },
    {
      text: "Datos del cliente",
      fontSize: 11,
      bold: true,
      color: "#0f172a",
      margin: [0, 0, 0, 8],
    },
    buildKeyValueTable([
      ["Cliente", order.customer?.nombres],
      ["Documento", order.customer?.numero_documento],
      ["Telefono", order.customer?.telefono],
    ]),
    {
      text: "Datos del equipo",
      fontSize: 11,
      bold: true,
      color: "#0f172a",
      margin: [0, 16, 0, 8],
    },
    buildKeyValueTable([
      ["Numero de entrada", order.codigo || `#${order.id}`],
      ["Equipo", equipmentName || order.tipo_equipo],
      ["Detalle", buildDetailsSummaryText(order)],
      ["Total estimado", formatMoney(resolveEstimatedTotal(order))],
      ["Total abonado", formatMoney(order.monto_pagado_total)],
      ["Color", order.color],
      ["IMEI", order.imei],
      ["Serial", order.serial],
      ["Accesorios", order.accesorios],
      ["Estado inicial", order.estado],
    ]),
    {
      text: "Problema reportado",
      fontSize: 11,
      bold: true,
      color: "#0f172a",
      margin: [0, 16, 0, 8],
    },
    {
      text: normalizeText(order.problema_reportado),
      margin: [8, 8, 8, 8],
      fillColor: "#f8fafc",
      color: "#334155",
    },
    {
      text: "Observaciones",
      fontSize: 11,
      bold: true,
      color: "#0f172a",
      margin: [0, 16, 0, 8],
    },
    {
      text: normalizeText(order.observaciones, "Sin observaciones registradas"),
      margin: [8, 8, 8, 8],
      fillColor: "#f8fafc",
      color: "#334155",
    },
    {
      text: "Seguimiento publico",
      fontSize: 11,
      bold: true,
      color: "#0f172a",
      margin: [0, 16, 0, 8],
    },
    buildKeyValueTable([
      ["Ruta", publicTrackingPath || "No disponible en reimpresion"],
      ["Enlace", publicTrackingUrl || "No disponible en reimpresion"],
    ]),
    {
      text: "Conserve este recibo para seguimiento y entrega del equipo.",
      margin: [0, 16, 0, 0],
      fontSize: 9,
      color: "#475569",
      italics: true,
    },
    {
      columns: [
        {
          width: "*",
          stack: [
            {
              text: "_______________________________",
              alignment: "center",
              margin: [0, 34, 0, 0],
            },
            {
              text: "Firma del cliente",
              alignment: "center",
              fontSize: 9,
              color: "#475569",
              margin: [0, 4, 0, 0],
            },
          ],
        },
        { width: 30, text: "" },
        {
          width: "*",
          stack: [
            {
              text: "_______________________________",
              alignment: "center",
              margin: [0, 34, 0, 0],
            },
            {
              text: "Recibido por",
              alignment: "center",
              fontSize: 9,
              color: "#475569",
              margin: [0, 4, 0, 0],
            },
          ],
        },
      ],
    },
  ],
  defaultStyle: {
    fontSize: 10,
    color: "#111827",
  },
});

const buildTicketDefinition = ({
  company,
  companyLogo,
  order,
  equipmentName,
}) => ({
  pageSize: { width: 226.77, height: 900 },
  pageMargins: [14, 16, 14, 18],
  content: [
    companyLogo
      ? {
          image: companyLogo,
          fit: [52, 52],
          alignment: "center",
          margin: [0, 0, 0, 6],
        }
      : { text: "" },
    ...buildCompanyLines(company, true).map((line, index) => ({
      text: line,
      alignment: "center",
      fontSize: index === 0 ? 9 : 7,
      bold: index === 0,
      color: index === 0 ? "#0f172a" : "#475569",
      margin: [0, index === 0 ? 0 : 1, 0, 0],
    })),
    {
      canvas: [
        {
          type: "line",
          x1: 0,
          y1: 0,
          x2: 198,
          y2: 0,
          lineWidth: 1,
          lineColor: "#cbd5e1",
        },
      ],
      margin: [0, 8, 0, 8],
    },
    {
      text: "RECIBO DE RECEPCION",
      alignment: "center",
      bold: true,
      fontSize: 9,
      color: "#0f172a",
    },
    {
      text: normalizeText(order.codigo, `Entrada #${order.id}`),
      alignment: "center",
      bold: true,
      fontSize: 11,
      color: "#1d4ed8",
      margin: [0, 3, 0, 0],
    },
    {
      text: formatDateTime(order.fecha_recepcion),
      alignment: "center",
      fontSize: 7,
      color: "#475569",
      margin: [0, 2, 0, 8],
    },
    buildKeyValueTable([
      ["Cliente", order.customer?.nombres],
      ["Telefono", order.customer?.telefono],
      ["Equipo", equipmentName || order.tipo_equipo],
      ["Detalle", buildDetailsSummaryText(order)],
      ["Estimado", formatMoney(resolveEstimatedTotal(order))],
      ["Abonado", formatMoney(order.monto_pagado_total)],
      ["IMEI", order.imei],
      ["Serial", order.serial],
      ["Estado", order.estado],
    ]),
    {
      text: "Problema reportado",
      bold: true,
      fontSize: 8,
      margin: [0, 8, 0, 4],
      color: "#0f172a",
    },
    {
      text: normalizeText(order.problema_reportado),
      fontSize: 7,
      color: "#334155",
      margin: [0, 0, 0, 6],
    },
    {
      text: "Observaciones",
      bold: true,
      fontSize: 8,
      margin: [0, 4, 0, 4],
      color: "#0f172a",
    },
    {
      text: normalizeText(order.observaciones, "Sin observaciones registradas"),
      fontSize: 7,
      color: "#334155",
      margin: [0, 0, 0, 8],
    },
    {
      text: "Conserve este recibo para retirar su equipo.",
      alignment: "center",
      italics: true,
      fontSize: 7,
      color: "#475569",
      margin: [0, 8, 0, 10],
    },
    {
      text: "__________________________",
      alignment: "center",
      margin: [0, 10, 0, 0],
    },
    {
      text: "Firma del cliente",
      alignment: "center",
      fontSize: 7,
      color: "#475569",
      margin: [0, 3, 0, 0],
    },
  ],
  defaultStyle: {
    fontSize: 8,
    color: "#111827",
  },
});

export const printWorkshopReceipt = async ({
  order,
  publicTrackingUrl,
  publicTrackingPath,
  storeId,
  format = "letter",
}) => {
  if (!order?.id) {
    errorActions(
      "No hay una orden válida para imprimir el comprobante de recepción.",
    );
    return;
  }

  try {
    const company = await getCompany(storeId || order.idsucursal);
    let companyLogo = null;

    try {
      if (company?.companyLogo) {
        companyLogo = await imageInBase64(company.companyLogo);
      }
    } catch (error) {
      companyLogo = null;
    }

    const equipmentName = [order.tipo_equipo, order.marca, order.modelo]
      .filter((value) => String(value || "").trim() !== "")
      .join(" - ");

    const docDefinition =
      format === "ticket"
        ? buildTicketDefinition({ company, companyLogo, order, equipmentName })
        : buildLetterDefinition({
            company,
            companyLogo,
            order,
            publicTrackingUrl,
            publicTrackingPath,
            equipmentName,
          });

    const pdfDocGenerator = pdfMake.createPdf(docDefinition);
    await createElementPDF(pdfDocGenerator, {
      enablePrintButton: false,
      focusPrintButton: false,
      enterToPrint: true,
      closeOnEscape: true,
      autoPrintOnLoad: true,
      hidePreview: true,
      minimalUI: true,
      autoCloseAfterPrint: true,
      managedPrintTab: true,
    });
  } catch (error) {
    errorActions("No se pudo generar el comprobante de recepción del taller.");
  }
};
